import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Const } from '../constants/Const.constant';
import { CallbackService } from './callback.service';
import { UserAgentService } from './user-agent.service';

@Injectable({
  providedIn: 'root',
})
export class NgrxHttpClient {
  protected http = inject(HttpClient);
  protected cookie = inject(CookieService);
  protected callBack = inject(CallbackService);
  protected agent = inject(UserAgentService);

  protected accessToken!: string;
  protected badTokenCallback = null;
  private readonly errors = new Subject<{ code: number } | boolean>();
  public errors$ = this.errors.asObservable();
  protected trackedErrors = [400, 404, 405, 500, 501, 0];

  post(endpoint: string, params = {}, apiUrl?, options?: any, header?: Record<string, string>): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.cookie.get(`${environment.cookiePrefix}access_token`)}`,

        ...header,
      }),
      ...options,
    };

    return this.http.post((apiUrl || environment.apiUrl) + endpoint.toLowerCase(), params, httpOptions).pipe(
      catchError((error) => {
        this.handleError(error);
        throw error;
      }),
    );
  }

  postSync(endpoint: string, params = {}, options?: any, header?: Record<string, string>): Observable<any> {
    return this.post(endpoint, params, environment.syncApiUrl, options, header);
  }

  get<T>(endpoint: string, params: any = {}, apiUrl?: string): Observable<T> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${this.cookie.get(`${environment.cookiePrefix}access_token`)}`,
    });

    return this.http
      .get<{ body: T }>((apiUrl || environment.apiUrl) + endpoint.toLowerCase(), {
        params,
        headers,
      })
      .pipe(
        map((response) => response.body),
        catchError((error) => {
          this.handleError(error);
          throw error;
        }),
      );
  }

  getSync(endpoint: string, params = {}): Observable<any> {
    return this.get(endpoint, params, environment.syncApiUrl);
  }

  patch(endpoint: string, params = {}, apiUrl?: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.cookie.get(`${environment.cookiePrefix}access_token`)}`,
      }),
    };

    return this.http
      .patch<{ body: object }>((apiUrl || environment.apiUrl) + endpoint.toLowerCase(), params, httpOptions)
      .pipe(
        map((response) => response.body),
        catchError((error) => {
          this.handleError(error);
          throw error;
        }),
      );
  }

  patchSync(endpoint: string, params = {}): Observable<any> {
    return this.patch(endpoint, params, environment.syncApiUrl);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  delete(endpoint: string, params = {}, apiUrl?: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.cookie.get(`${environment.cookiePrefix}access_token`)}`,
      }),
    };
    return this.http
      .delete<{ body: object }>((apiUrl || environment.apiUrl) + endpoint.toLowerCase(), httpOptions)
      .pipe(
        map((response) => response.body),
        catchError((error) => {
          this.handleError(error);
          throw error;
        }),
      );
  }

  deleteSync(endpoint: string, params = {}): Observable<any> {
    return this.delete(endpoint, params, environment.syncApiUrl);
  }

  vpnCall(method: string, url: string, params = {}, preventGlobalError: boolean = false): Observable<any> {
    if (typeof url === 'undefined' || url.includes('undefined')) {
      this.handleError(new Error());
    }

    return this.http.get<{ body: object }>(`${url}/command/${method}`, { params }).pipe(
      map((response: any) => {
        return response.body || response.conf || response;
      }),
      catchError((error) => {
        this.handleError(error, preventGlobalError);
        throw error;
      }),
    );
  }

  authorizeUri(params = {}): void {
    // Add access token
    params['access_token'] = this.cookie.get(`${environment.cookiePrefix}access_token`);

    let httpParams = new HttpParams();
    Object.keys(params).map((key) => {
      httpParams = httpParams.set(key, params[key]);
    });

    const url = `${environment.oauth2ApiUrl}authorize?${httpParams.toString()}`;
    window.open(url, '_self');
  }

  getPublic(endpoint: string, apiUrl: string, params: any = {}): Observable<any> {
    // get data without access token
    return this.http
      .get(apiUrl + endpoint, {
        params,
      })
      .pipe(
        map((response) => response),
        catchError((error) => {
          this.handleError(error);
          throw error;
        }),
      );
  }

  handleError(err, preventGlobalError = false): void {
    try {
      if (err.error.error !== undefined && err.error.error.code !== undefined) {
        switch (err.error.error.code) {
          case Const.NARestErrorCode.ACCESS_TOKEN_MISSING:
          case Const.NARestErrorCode.INVALID_ACCESS_TOKEN:
          case Const.NARestErrorCode.ACCESS_TOKEN_EXPIRED:
            this.handleTokenError();
            break;
          default:
        }
      }

      if (this.trackedErrors.includes(err.status) && !preventGlobalError) {
        this.errors.next({ code: err.status });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('ApiModuleNg2 Unknown error', err);
      throw new Error('Unknown error');
    }

    console.error('ApiModuleNg2 error', err);
    throw err;
  }

  handleTokenError(): void {
    this.callBack.call('tokenExpired', undefined);
    if (!this.agent.isNativeApp()) {
      window.location.href = `${environment.authUrl}/checklogin?next_url=${encodeURIComponent(window.location.href)}`;
    }
  }

  dismissError(): void {
    this.errors.next(false);
  }
}
